import React, { useMemo } from 'react';
import { Button } from '@veneer/core';
import { useFeatureFlagContext } from '@eu-data-act/react-euda-commons/dist/index';
import { useDependencyManagerContext } from '../../../contexts/dependencyManager';
import { OperationStatus } from '../../../types';
import { ScreenReaderOnly } from '../styled';

type TelemetryDownloadButtonProps = {
  'data-testid': string;
  onDownloadClick: () => void;
  telemetryRequestStatus: OperationStatus;
  deviceFamily: string;
  serialNumber: string;
  tabIndex?: number;
};

const TelemetryDownloadButton: React.FC<TelemetryDownloadButtonProps> = ({
  'data-testid': dataTestId,
  onDownloadClick,
  deviceFamily,
  serialNumber,
  tabIndex
}) => {
  const { translate } = useDependencyManagerContext();
  const { releasePlatformHpAnalytics } = useFeatureFlagContext();

  const buttonTextKey =
    'components.device-item.telemetry-download-button.download-data';
  const ariaLabelKey =
    'components.device-item.telemetry-download-button.aria-label';
  const ariaDescriptionKey =
    'components.device-item.telemetry-download-button.aria-description';

  const unknownDeviceName = translate('components.device-item.no-data.unknown');

  const downloadButtonAnalyticsAttributes = useMemo(
    () =>
      releasePlatformHpAnalytics
        ? {
            'data-gtm-category': 'linkClick',
            'data-gtm-id': `device-card-download-button-${serialNumber}`,
            'data-gtm-value': `download-data-${deviceFamily}-${serialNumber}`
          }
        : {},
    [serialNumber, deviceFamily, releasePlatformHpAnalytics]
  );

  return (
    <Button
      colorScheme="positive"
      data-testid={dataTestId}
      onClick={onDownloadClick}
      tabIndex={tabIndex}
      aria-label={translate(ariaLabelKey, {
        deviceFamily: deviceFamily || unknownDeviceName,
        serialNumber
      })}
      aria-describedby={`${dataTestId}-description`}
      {...downloadButtonAnalyticsAttributes}
    >
      {translate(buttonTextKey)}
      <ScreenReaderOnly
        id={`${dataTestId}-description`}
        aria-live="polite"
      >
        {translate(ariaDescriptionKey, {
          deviceFamily: deviceFamily || unknownDeviceName,
          serialNumber
        })}
      </ScreenReaderOnly>
    </Button>
  );
};

export default TelemetryDownloadButton;
